import React from 'react';

import { Navigate } from 'react-router-dom';
import { observer } from 'mobx-react';

import Record from 'ui/Record/Record';

import { MainHeader } from 'components/MainHeader/MainHeader';

import { VatixError } from 'components/Error/types';

import { useStore } from 'utils/hooks/store';

import { UserRole } from 'core/constants';

import { useNavBarHeight } from 'utils/hooks/navbar';

import routes from 'core/routes';
import { canAccessRisksProduct } from 'utils/roles/permissions';

import NewActionMenu from './components/NewActionMenu/NewActionMenu';
import TasksTable from './components/TasksTable';
import AuditsTab from './components/AuditsTab';
import AcknowledgementsTab from './components/AcknowledgementsTab/AcknowledgementsTab';
import RiskActionsTab from './components/RiskActionsTab/RiskActionsTab';

export type ActionsSurface = 'tasks' | 'audits' | 'acknowledgements' | 'risks';

const SURFACE_TITLE: Record<ActionsSurface, string> = {
  tasks: 'Tasks',
  audits: 'Audits',
  acknowledgements: 'Acknowledgements',
  risks: 'Risks',
};

const SURFACE_LINK: Record<ActionsSurface, string> = {
  tasks: routes.dashboard.tasks.list.toString(),
  audits: routes.dashboard.tasks.audits.toString(),
  acknowledgements: routes.dashboard.tasks.acknowledgements.toString(),
  risks: routes.dashboard.tasks.risks.toString(),
};

const Tasks: React.FunctionComponent<{ surface: ActionsSurface }> = observer(({ surface }): React.ReactElement => {
  const { session } = useStore();
  const refreshRef = React.useRef<Function | null>(null);
  const navBarHeight = useNavBarHeight();

  const hasAuditsLicense = (session.user?.licenses?.workflows && session.user?.scheduleEnabled) ?? false;
  // Flag + Documents licence: only licence holders can be assigned acknowledgements, so a
  // non-licence user has nothing to show (see §Permissions).
  const hasAcknowledgements =
    (session.user?.documentAcknowledgementsEnabled && session.user?.licenses?.documents) ?? false;

  const onRefreshCallback = (callback: Function): void => {
    refreshRef.current = callback;
  };

  const handleRefresh = (): void => {
    refreshRef.current?.();
  };

  // A surface the user isn't entitled to falls back to Tasks rather than the Unauthorized page.
  if (surface === 'audits' && !hasAuditsLicense) {
    return <Navigate replace to={routes.dashboard.tasks.list.toString()} />;
  }
  if (surface === 'acknowledgements' && !hasAcknowledgements) {
    return <Navigate replace to={routes.dashboard.tasks.list.toString()} />;
  }
  if (surface === 'risks' && !canAccessRisksProduct(session.user)) {
    return <Navigate replace to={routes.dashboard.tasks.list.toString()} />;
  }

  const renderBody = (onError: (error: VatixError) => void): React.ReactElement => {
    if (surface === 'audits') {
      return <AuditsTab onError={onError} onRefreshCallback={onRefreshCallback} />;
    }
    if (surface === 'acknowledgements') {
      return <AcknowledgementsTab onError={onError} onRefreshCallback={onRefreshCallback} />;
    }
    if (surface === 'risks') {
      return <RiskActionsTab onError={onError} />;
    }
    return <TasksTable onError={onError} onRefreshCallback={onRefreshCallback} />;
  };

  // Recipients can't create acknowledgements (creation is owner-side, from Documents), so the
  // "New" action is hidden entirely on that surface; task/audit creation stays hidden for User
  // and for read-only users, whose writes the API rejects.
  const showNewAction =
    surface !== 'acknowledgements' &&
    surface !== 'risks' &&
    session.user?.role !== UserRole.User &&
    !session.user?.readOnly;

  return (
    <Record
      navBarHeight={navBarHeight}
      Content={(onError) => renderBody(onError)}
      Header={
        <MainHeader
          title={SURFACE_TITLE[surface]}
          breadcrumbs={[
            { label: 'Actions', link: SURFACE_LINK[surface] },
            { label: SURFACE_TITLE[surface], link: SURFACE_LINK[surface] },
          ]}
          action={
            showNewAction ? (
              <NewActionMenu activeTab={surface === 'audits' ? 'audits' : 'tasks'} onRefresh={handleRefresh} />
            ) : undefined
          }
        />
      }
    />
  );
});

export default Tasks;
